import type { ReactElement } from 'react';
import { capitalize } from 'utils/strings';
import { toCurrency } from 'utils/numbers';

import MealTypes from 'models/MealTypes';
import MenuItem from 'models/MenuItem';

import SkuTags from 'components/shared/SkuTags';
import RecipePrice from '../../Cart/Recipes/RecipePrice';

type Props = {
  item: MenuItem;
  cartId: number | null;
  openProductInNewTab: boolean;
};

function SkuContent({
  item,
  cartId,
  openProductInNewTab,
}: Props): ReactElement {
  const {
    mealType,
    quantity,
    isSnack,
    isMealKit,
    isPreparedMeal,
    isBundle,
    isGroceryCategory,
    isOnSale,
    price,
    componentSkusQuantity,
    sku,
  } = item;
  const mealTypeName = capitalize(MealTypes[mealType].label);
  const {
    title,
    tags,
    subtitle,
    servings,
    calories,
    brand,
    link_to_product: linkToProduct,
    prep_and_cook_time: prepAndCookTime,
    subscription_price_per_serving: subscriptionPricePerServing,
    product_type_name: productTypeName,
    is_lto_box: isLTOBox,
  } = item.sku;

  const orderHistoryPrice = Number(price) / quantity;
  const hasRteProductServings = window.gon?.featureFlags?.hasRteProductServings;
  const pdpLink = cartId
    ? `${linkToProduct}?cart_id=${cartId}`
    : linkToProduct;
  const isLTO = sku.is_lto_box;

  return (
    <>
      <div className="c-recipe__content--center">
        <div className="c-recipe__type">
          {isLTO ? '4-item box, feeds 2-4'
            : (
              <>
                {isBundle && `${componentSkusQuantity} Item `}
                {isPreparedMeal && `${servings} Serving `}
                {`${productTypeName} `}
                {!isGroceryCategory && '/ '}
                {isGroceryCategory && brand && `/ ${brand}® `}
                {isMealKit && `${servings} Serving `}
                {isPreparedMeal && `${quantity} Count `}
                {isMealKit && `${mealTypeName} `}
                {!isPreparedMeal && quantity > 1 && `x ${quantity}`}
              </>
            )}
        </div>

        <a
          className="c-recipe__title"
          target={openProductInNewTab ? '_blank' : '_self'}
          rel="noreferrer"
          href={isLTOBox ? '/lto/robin-arzon' : pdpLink}
        >
          <h3>
            {isLTO ? 'Eat To Hustle x Purple Carrot' : title}
            <span className="c-recipe__subtitle">
              {subtitle}
            </span>
          </h3>
        </a>
      </div>

      <div className="c-recipe__content--bottom">
        {tags.length > 0 && <SkuTags tags={tags} />}

        <div className="c-recipe__info">
          {!isSnack && (
            <>
              {!!prepAndCookTime && !isPreparedMeal && (
                <>
                  <span className="c-recipe__time">
                    {`${prepAndCookTime} min`}
                  </span>

                  {' / '}
                </>
              )}

              {!!calories && (
                <span className="c-recipe__calories">
                  {!!calories && `${calories} cal`}
                </span>
              )}
              {hasRteProductServings ? (
                <>
                  {!!calories && ' / '}
                  <span className="c-recipe__price">
                    {isPreparedMeal && `${toCurrency(subscriptionPricePerServing * (servings ?? 0))} each`}
                    {!isPreparedMeal && `${toCurrency(subscriptionPricePerServing)} per serving`}
                  </span>
                </>
              ) : (
                <>
                  {!isBundle && (
                    <>
                      {!!calories && ' / '}
                      <span className="c-recipe__price">
                        {isOnSale && item.sale_price_per_serving ? (
                          <>
                            <span className="c-recipe__sale-price">
                              {toCurrency(item.sale_price_per_serving)}
                            </span>
                            <span className="c-recipe__pre-sale-price">
                              {toCurrency(subscriptionPricePerServing)}
                            </span>
                          </>
                        ) : (
                          <span style={{
                            ...(item.premium
                              ? {
                                color: '#31544E',
                                fontWeight: 800,
                              } : {}),
                          }}
                          >
                            {toCurrency(subscriptionPricePerServing)}
                          </span>
                        )}
                        {' '}
                        per serving
                      </span>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {isGroceryCategory && (
            <RecipePrice
              item={item}
              price={orderHistoryPrice}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default SkuContent;
